/**
 * CMS Hero utilities
 * Управление hero-блоками страниц: тексты, кнопки, медиагалерея
 */

import { getCms, preloadPageCms } from './cms';
import { cmsBlocksDb } from './adminDb';
import { getCachedHeroMedia, saveHeroMediaToCache } from './globalDataCache';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface HeroMediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
  order: number;
}

export interface HeroTexts {
  eyebrow: string;
  title: string;
  subtitle: string;
}

export interface HomeHeroData extends HeroTexts {
  cta1Text: string;
  cta1Url: string;
  cta2Text: string;
  cta2Url: string;
  media: HeroMediaItem[];
}

// ─── Media storage key ──────────────────────────────────────────────────────

const MEDIA_BLOCK_KEY = 'hero.media';
const MEDIA_BLOCK_ID = `home__${MEDIA_BLOCK_KEY}___json`;

// ─── Media CRUD ─────────────────────────────────────────────────────────────

export async function getHeroMedia(): Promise<HeroMediaItem[]> {
  // Try local cache first for instant response
  const cached = getCachedHeroMedia();
  if (cached && cached.length > 0) return cached;

  try {
    // Targeted query: fetch only home-page blocks instead of the whole cmsBlocks table
    const result = await cmsBlocksDb.getByPage('home');
    if (!result.ok) return [];
    const record = result.data.find(
      (b: any) => b.blockKey === MEDIA_BLOCK_KEY
    );
    if (!record?.contentJson) return [];
    const parsed = JSON.parse(record.contentJson);
    if (!Array.isArray(parsed)) return [];
    
    // Save to cache for next time
    saveHeroMediaToCache(parsed);
    
    return parsed as HeroMediaItem[];
  } catch {
    return [];
  }
}

export async function saveHeroMedia(items: HeroMediaItem[]): Promise<void> {
  // Update local cache immediately
  saveHeroMediaToCache(items);

  await cmsBlocksDb.upsert({
    id: MEDIA_BLOCK_ID,
    pageKey: 'home',
    blockKey: MEDIA_BLOCK_KEY,
    lang: '_json',
    contentJson: JSON.stringify(items),
    updatedAt: new Date().toISOString(),
  });
}

// ─── Hero texts for page (preloaded) ────────────────────────────────────────

export async function getHeroTexts(
  pageKey: string,
  lang: string,
  fallbacks: { eyebrow?: string; title?: string; subtitle?: string } = {}
): Promise<HeroTexts> {
  await preloadPageCms(pageKey);
  const [eyebrow, title, subtitle] = await Promise.all([
    getCms(pageKey, 'hero.eyebrow', lang, fallbacks.eyebrow ?? ''),
    getCms(pageKey, 'hero.title', lang, fallbacks.title ?? ''),
    getCms(pageKey, 'hero.subtitle', lang, fallbacks.subtitle ?? ''),
  ]);
  return { eyebrow, title, subtitle };
}

export async function getHomeHeroData(
  lang: string,
  fallbacks: {
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    cta1Text?: string;
    cta1Url?: string;
    cta2Text?: string;
    cta2Url?: string;
  } = {}
): Promise<HomeHeroData> {
  await preloadPageCms('home');
  const [eyebrow, title, subtitle, cta1Text, cta1Url, cta2Text, cta2Url, media] =
    await Promise.all([
      getCms('home', 'hero.eyebrow', lang, fallbacks.eyebrow ?? ''),
      getCms('home', 'hero.title', lang, fallbacks.title ?? ''),
      getCms('home', 'hero.subtitle', lang, fallbacks.subtitle ?? ''),
      getCms('home', 'hero.cta1.text', lang, fallbacks.cta1Text ?? ''),
      getCms('home', 'hero.cta1.url', lang, fallbacks.cta1Url ?? '/catalog'),
      getCms('home', 'hero.cta2.text', lang, fallbacks.cta2Text ?? ''),
      getCms('home', 'hero.cta2.url', lang, fallbacks.cta2Url ?? '/about'),
      getHeroMedia(),
    ]);
  return { eyebrow, title, subtitle, cta1Text, cta1Url, cta2Text, cta2Url, media };
}